import { useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { paymentMethodTypes } from '@JohnLewisPartnership/checkout-psp-ui';

// Design System
import { PrimaryButton } from 'jl-design-system/elements/button/Button';
import env from 'jl-design-system/utils/env/env';

// Types
import { AppDispatch } from 'types/RootState.types';

// Config
import googlePayButtonState from './GooglePayButton.state';
import paymentTypeConstants from '@constants/paymentTypeConstants';
import { handleGooglePayButtonClick, openGooglePayStubsModal } from '@redux/actions/payment/googlePayActions';
import { setSelectedPaymentType, triggerPlaceOrderAndPayStartAnalytics } from '@redux/actions/payment/paymentActions';
import { saveTimePlaceOrderClicked } from '@redux/actions/timing/timingActions';
import { sendNewRelicCustomEvent } from '@utils/logging/logging-utils';

// components
import Container from '@components/container';
import StripePaymentProvider from '@components/payments/stripe/stripe-payment-provider';
import GooglePayStubsModal from '@non-prod-utils/components/google-pay-stubs-modal';

// styles
import styles from './google-pay-button.scss';

const GooglePayButton = () => {
  const dispatch: AppDispatch = useDispatch();
  const { cardPaymentMethod, googlePayStubsModalOpen, googlePayConfiguration, sessionId, stripeCheckoutFlowEnabled } =
    useSelector(googlePayButtonState);

  const { environment, merchantInfo } = googlePayConfiguration ?? {};

  // change this to use real google pay locally
  const useRealGooglePay = () => !env.isClientLocal && !env.isClientDev;

  const onClick = () => {
    dispatch(saveTimePlaceOrderClicked());
    dispatch(triggerPlaceOrderAndPayStartAnalytics('gpay'));
    dispatch(setSelectedPaymentType({ paymentType: paymentTypeConstants.GOOGLE_PAY }));

    if (useRealGooglePay()) {
      dispatch(handleGooglePayButtonClick());
    } else {
      dispatch(openGooglePayStubsModal());
    }
  };

  const onReadyHandler = (isReady: boolean) => {
    if (!isReady) {
      dispatch(sendNewRelicCustomEvent('Google Pay not available in Stripe flow', { sessionId }));
    }
  };

  useEffect(() => {
    if (!stripeCheckoutFlowEnabled) {
      const paymentsClient = new window.google.payments.api.PaymentsClient({ environment, merchantInfo });

      if (useRealGooglePay()) {
        const button = paymentsClient.createButton({
          allowedPaymentMethods: [cardPaymentMethod],
          buttonSizeMode: 'fill',
          onClick,
        });

        const buttonContainer = document.getElementById('google-pay-button');

        if (buttonContainer) {
          buttonContainer.append(button);
        }
      }
    }
  }, []);

  // remove env.isClientLocal to use real google pay locally
  const shouldUseStubs = env.isClientLocal || env.isClientDev;

  if (shouldUseStubs) {
    return (
      <>
        {stripeCheckoutFlowEnabled ? (
          <StripePaymentProvider
            id="googlePayButton"
            isStubbed
            onReady={onReadyHandler}
            paymentMethodType={paymentMethodTypes.GOOGLE_PAY}
          />
        ) : (
          <>
            <PrimaryButton className={styles.button} data-testid="google-pay-submit-button" onClick={onClick}>
              Pay with Google Pay
            </PrimaryButton>
            {googlePayStubsModalOpen && <GooglePayStubsModal />}
          </>
        )}
      </>
    );
  }

  return (
    <Container marginLeft="auto" relative>
      {stripeCheckoutFlowEnabled ? (
        <StripePaymentProvider
          id="googlePayButton"
          onReady={onReadyHandler}
          paymentMethodType={paymentMethodTypes.GOOGLE_PAY}
        />
      ) : (
        <div id="google-pay-button" />
      )}
    </Container>
  );
};

export default GooglePayButton;
